import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './EmailVerification.css'; // Reuse the same styling

const ResendVerification = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(''); // 'success', 'error'
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setStatus('');
    setMessage('');

    try {
      console.log('Sending resend verification request for:', email);

      // Direct axios call since this endpoint is now exempt from CSRF
      const response = await axios.post('/api/email-verification/resend', {
        email: email
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('Resend verification response:', response.data);

      if (response.data && response.data.success) {
        setStatus('success');
        setMessage(response.data.message || 'Verification email sent successfully!');

        // Clear the form
        setEmail('');

        // Redirect to login after showing success message for a moment
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Verification email sent! Please check your inbox and spam folder.',
              type: 'success'
            }
          });
        }, 3000);
      } else {
        setStatus('error');
        setError('Failed to send verification email. Please try again.');
      }
    } catch (err) {
      console.error('Resend verification error:', err);

      if (err.response) {
        console.error('Error response:', err.response.data);
        setStatus('error');
        setError(err.response.data.message || 'Failed to send verification email. Please try again.');
      } else if (err.request) {
        setStatus('error');
        setError('Network error. Please check your connection and try again.');
      } else {
        setStatus('error');
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="email-verification-container">
      <div className="verification-card">
        <div className="verification-header">
          <h1>🐰 White Rabbit</h1>
          <h2>Resend Verification Email</h2>
        </div>

        <div className="verification-content">
          {status !== 'success' && (
            <div className="resend-form">
              <p>Enter your email address to receive a new verification link.</p>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    disabled={isLoading}
                    required
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <span className="error-icon">❌</span>
                    {error}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="resend-button"
                    disabled={isLoading || !email}
                  >
                    {isLoading ? (
                      <>
                        <div className="loading-spinner small"></div>
                        Sending...
                      </>
                    ) : (
                      'Send Verification Email'
                    )}
                  </button>

                  <Link to="/login" className="back-to-login">
                    Back to Login
                  </Link>
                </div>
              </form>

              <div className="help-text">
                <h4>Tips:</h4>
                <ul>
                  <li>Check your spam/junk folder if you don't see the email</li>
                  <li>You can only request a new verification email every 5 minutes</li>
                  <li>Verification links are valid for 48 hours</li>
                  <li>If you continue to have issues, contact support@whiterabbit.icu</li>
                </ul>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="verification-status success">
              <div className="success-icon">✅</div>
              <h3>Verification Email Sent!</h3>
              <p>{message}</p>
              <p className="redirect-info">Please check your inbox and spam folder. Redirecting to login in 3 seconds...</p>
              <div className="form-actions">
                <Link to="/login" className="login-button">
                  Go to Login Now
                </Link>
                <button
                  onClick={() => {
                    setStatus('');
                    setMessage('');
                    setError('');
                  }}
                  className="resend-another"
                >
                  Send to Another Email
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="verification-footer">
          <p>
            Don't have an account? <Link to="/login">Sign up here</Link>
          </p>
          <p>Need help? Contact support@whiterabbit.icu</p>
        </div>
      </div>
    </div>
  );
};

export default ResendVerification;